import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../assets/table-style";
import BASE_URL from "../../../assets/global/url";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../../assets/img/NoAccess.png";
import swal from "sweetalert";
import { format } from "date-fns";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { Tabs, Tab } from "react-bootstrap";
import DateRangePicker from "../../../components/DateRangePicker";
import { getMonthBoundaries, initializeCutoff } from "../../../utils/newdate";
import FilterStore from "../../../hooks/customHook/FilterStore";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";
import { formatDateForAPI } from "../../../utils/dateFormatter";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { useServerSort } from "../../../hooks/customHook/sortingHook/useTableSort";

const boundaries = getMonthBoundaries();

const NewProduction = ({ authrztn }) => {
  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();
  const pageRestored = useRef(false);

  const [searchText, setSearchText] = useState(() =>
    FilterStore.NPgetSearch(`NewProduction`),
  );
  const [filterColumn, setFilterColumn] = useState(() =>
    FilterStore.NPgetFilterColumn(),
  );

  const validtabs = ["Pending", "Approved", "Rejected", "All"];
  const [activeKeyTab, setActiveKeyTab] = useState(() => {
    const saved = FilterStore.getTab();
    return validtabs.includes(saved) ? saved : "Pending";
  });

  const [vendors_db, setVendors_db] = useState([]);
  const [fetchProduction, setFetchProduction] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [dateRange, setDateRange] = useState(() => {
    const savedFilter = FilterStore.get();

    if (savedFilter && savedFilter.from && savedFilter.to) {
      return {
        start: formatDateForAPI(savedFilter.from),
        end: formatDateForAPI(savedFilter.to),
      };
    }

    return {
      start: formatDateForAPI(boundaries.start),
      end: formatDateForAPI(boundaries.end),
    };
  });

  const debounceTimer = useRef(null);

  const [widgetData, setWidgetData] = useState({
    totalRawMaterials: 0,
    totalProduce: 0,
    averageLoss: 0,
  });

  useEffect(() => {
    FilterStore.NPsaveSearch(searchText, `NewProduction`);
  }, [searchText]);

  useEffect(() => {
    FilterStore.NPsaveFilterColumn(filterColumn);
  }, [filterColumn]);

  const pagination = useServerPagination(
    BASE_URL + "/production/getDataProductionNew",
    FilterStore.NPgetPerPage(),
    {
      startDate: dateRange.start,
      endDate: dateRange.end,
      status: activeKeyTab || "Pending",
    },
  );

  // No initial sortBy: the backend default (newest first) has no visible column,
  // so every header starts neutral until the user picks one
  const sort = useServerSort(pagination);

  useEffect(() => {
    if (!pagination.loading) {
      setIsLoading(false);
    }
  }, [pagination.loading]);

  // --- Page / PerPage retention ---
  useEffect(() => {
    if (!pageRestored.current) return;
    if (pagination.currentPage) {
      FilterStore.NPsavePage(pagination.currentPage);
    }
  }, [pagination.currentPage]);

  useEffect(() => {
    if (pagination.itemsPerPage) {
      FilterStore.NPsavePerPage(pagination.itemsPerPage);
    }
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    const savedPage = FilterStore.NPgetPage();
    if (savedPage > 1) {
      setTimeout(() => {
        pagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 600);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWidgetData = useCallback(async (startDate, endDate, status) => {
    if (!startDate || !endDate) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/production/getProductionWidget`,
        {
          params: {
            startDate: formatDateForAPI(startDate),
            endDate: formatDateForAPI(endDate),
            status,
          },
        },
      );
      setWidgetData(res.data);
    } catch (error) {
      console.error("Error fetching widget summary:", error);
    }
  }, []);

  useEffect(() => {
    fetchWidgetData(dateRange.start, dateRange.end, activeKeyTab);
  }, [dateRange.start, dateRange.end, activeKeyTab, fetchWidgetData]);

  const handleTabSelect = (key) => {
    setActiveKeyTab(key);
    pagination.updateParams({
      startDate: dateRange.start,
      endDate: dateRange.end,
      status: key,
      searchText,
      filterColumn,
    });
    FilterStore.saveTab(key);
  };

  const fetchVendorsData = () => {
    axios
      .get(BASE_URL + "/vendors/fetchVendors")
      .then((res) => {
        setVendors_db(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchVendorsData();
  }, []);

  const handleUpdate = (row) => {
    navigate(`/inventory/ProductionsView/${row.id}`);
  };

  const columns = [
    {
      name: "Production ID",
      selector: (row) => (
        <div title={row.production_id}>{row.production_id}</div>
      ),
      sortField: "production_id",
    },
    {
      name: "Product Description",
      selector: (row) => (row.desc === null ? "n/a" : row.desc),
      sortField: "desc",
    },
    {
      name: "Date Produce",
      selector: (row) => format(row.date_produce, "MMM/dd/yyyy"),
      sortField: "date_produce",
    },
    {
      name: "Qty In",
      selector: (row) => formatToTwoDecimal(row.total_quantity),
      sortField: "total_quantity",
    },
    {
      name: "Qty Produce",
      selector: (row) => formatToTwoDecimal(row.total_produce),
      sortField: "total_produce",
    },
    {
      name: "Loss (%)",
      selector: (row) => (
        <span style={{ color: "var(--widget-number-color-red)" }}>
          {formatToTwoDecimal(row.loss_percent)}%
        </span>
      ),
      sortField: "loss_percent",
    },
    {
      name: "Loss (Qty)",
      selector: (row) => (
        <span style={{ color: "var(--widget-number-color-red)" }}>
          {formatToTwoDecimal(row.loss_quantity)}
        </span>
      ),
      sortField: "loss_quantity",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortField: "status",
      cell: (row) => {
        let fontColor;

        switch (row.status) {
          case "Pending":
            fontColor = "var(--widget-number-color-orange)";
            break;
          case "Approved":
            fontColor = "#3B9F3F";
            break;
          case "Rejected":
            fontColor = "var(--widget-number-color-red)";
            break;
          default:
            fontColor = "#000000";
            break;
        }
        return (
          <div
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color: fontColor,
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
            className="text-center"
          >
            {" "}
            {row.status}
          </div>
        );
      },
    },
  ];

  if (authrztn.includes("Productions-Delete")) {
    columns.push({
      name: "Action",
      selector: (row) => (
        <i
          className="fas fa-trash"
          style={{ cursor: "pointer", color: "red", fontSize: "1.5rem" }}
          onClick={() => {
            handleDeleteProduction(
              row.id,
              row.date_produce,
              row.production_id,
              row.status,
              row.warehouse_id,
            );
          }}
        ></i>
      ),
    });
  }

  const handleSearch = (value) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      if (value.trim() === "") {
        pagination.updateApiUrl(`${BASE_URL}/production/getDataProductionNew`);
      } else {
        pagination.updateApiUrl(
          `${BASE_URL}/production/getDataProductionNew/search`,
        );
      }
      pagination.updateParams({
        startDate: dateRange.start,
        endDate: dateRange.end,
        searchText: value,
        filterColumn,
        status: activeKeyTab,
      });
    }, 500);

    setSearchText(value);
  };

  const handleDeleteProduction = async (
    primary_id,
    date_produce,
    production_code,
    status,
    warehouseId,
  ) => {
    swal({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios.delete(
            `${BASE_URL}/production/deleteProduction/`,
            {
              params: {
                primary_id,
                date_produce,
                production_code,
                status,
                warehouseId,
                userLoggedID,
              },
            },
          );
          if (response.status === 200) {
            swal({
              title: "Production Deleted Successfully!",
              text: "The production has been successfully deleted.",
              icon: "success",
              button: "OK",
            }).then(() => {
              pagination.refreshData();
            });
          } else if (response.status === 201) {
            swal({
              title: "Delete Prohibited!",
              text: `Production date (${format(new Date(response.data.date_produce), "MMM/dd/yyyy h:mm a")}) cannot be deleted as its payable date falls within the posted cutoff period named "${response.data.cutoff_name}".`,
              icon: "warning",
              button: "OK",
            });
          } else if (response.status === 202) {
            const { sales_number } = response.data;

            const title = document.createElement("div");
            const text = document.createElement("div");
            const swalInfo = document.createElement("div");

            title.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>`;
            text.innerHTML = `<div style="text-align: center; margin-top: 10px;">
                              Delete first the sales in module <strong>${`Sales Invoice`}</strong> with 
                              Transaction Number: <strong>${sales_number}</strong>
                            </div>`;

            swalInfo.append(title);
            swalInfo.append(text);
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else if (response.status === 203) {
            const { transaction_id } = response.data;

            const title = document.createElement("div");
            const text = document.createElement("div");
            const swalInfo = document.createElement("div");

            title.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>`;
            text.innerHTML = `<div style="text-align: center; margin-top: 10px;">
                              Delete first the stock transfer in module <strong>${`Stock Transfer`}</strong> with 
                              Transaction Number: <strong>${transaction_id}</strong>
                            </div>`;

            swalInfo.append(title);
            swalInfo.append(text);
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else {
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact our support team for assistance.",
            });
          }
        } catch (error) {
          if (error.response.status === 401) {
            swal({
              icon: "error",
              // title: "Please contact your support",
              title: error.response.data.title,
              text: error.response.data.message,
            }); // test 1
          } else {
            console.log(error);
            swal({
              icon: "error",
              title: "Error",
              text: "An error occurred while deleting the invoice.",
            });
          }
        }
      }
    });
  };

  // Filter the production data based on search text and date range
  // const filteredItems = fetchProduction.filter((item) => {
  //   const searchLower = searchText.toLowerCase();

  //   // Check if item falls within the date range
  //   const itemDate = new Date(item?.createdAt);
  //   const fromDateObj = fromDate ? new Date(fromDate) : null;
  //   const toDateObj = toDate ? new Date(toDate) : null;

  //   // Function to remove time component from date
  //   const normalizeDate = (date) =>
  //     new Date(date.getFullYear(), date.getMonth(), date.getDate());

  //   const normalizedItemDate = normalizeDate(itemDate);
  //   const normalizedFromDate = fromDateObj ? normalizeDate(fromDateObj) : null;
  //   const normalizedToDate = toDateObj ? normalizeDate(toDateObj) : null;

  //   // If there are valid "From" and "To" dates, filter by date range
  //   if (normalizedFromDate && normalizedItemDate < normalizedFromDate)
  //     return false;
  //   if (normalizedToDate && normalizedItemDate > normalizedToDate) return false;

  //   // Search text filter
  //   if (searchText) {
  //     switch (filterColumn) {
  //       case "production_id":
  //         return item?.production_id
  //           ?.toString()
  //           .toLowerCase()
  //           .includes(searchLower);
  //       case "desc":
  //         return item?.desc === null
  //           ? "n/a".includes(searchLower)
  //           : item?.desc.toLowerCase().includes(searchLower);
  //       case "date_created":
  //         const formattedDate = formatDatetime(item?.createdAt).toLowerCase();
  //         return formattedDate.includes(searchLower);
  //       default:
  //         return (
  //           item?.production_id
  //             ?.toString()
  //             .toLowerCase()
  //             .includes(searchLower) ||
  //           (item?.desc === null
  //             ? "n/a".includes(searchLower)
  //             : item?.desc.toLowerCase().includes(searchLower)) ||
  //           formatDatetime(item?.createdAt).toLowerCase().includes(searchLower)
  //         );
  //     }
  //   }

  //   return true;
  // });

  // Add a function to determine status
  // const getStatus = (row) => {
  //   return row.status ? row.status.toLowerCase() : "pending";
  // };

  // Filter fetchProduction based on active tab
  // const filteredByStatus = fetchProduction?.filter((row) => {
  //   if (activeKeyTab === "all") return true;
  //   return getStatus(row) === activeKeyTab;
  // });

  const handleDateRangeChange = (startDate, endDate) => {
    let from = startDate;
    let to = endDate;

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      from = start;
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      to = end;
    }

    const formattedStart = formatDateForAPI(from);
    const formattedEnd = formatDateForAPI(to);

    setDateRange({ start: formattedStart, end: formattedEnd });

    FilterStore.save(formattedStart, formattedEnd);

    pagination.updateParams({
      startDate: formattedStart,
      endDate: formattedEnd,
    });
  };

  return (
    <div className="h-100 w-100 bg-white">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("Productions-View") ? (
        <>
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">PRODUCTION</span>
            </div>
            <div>
              {authrztn.includes("Productions-Add") && (
                <Link
                  to="/inventory/productions-form"
                  className="btn btn-primary d-flex flex-row align-items-center title-button"
                >
                  <i className="bx bx-plus fs-5"></i> Create
                </Link>
              )}
            </div>
          </div>

          <div className="row p-2 mx-auto">
            <div className="col-sm w-100 p-3 payable-card">
              <div className="w-100 border p-3 shadow-sm rounded h-100">
                <div className=" d-flex flex-row align-items-center payable-icon">
                  <i className="bx bx-bar-chart-alt fs-3 h-100"></i>
                  <h3>Total Raw Materials</h3>
                </div>
                <div className=" mt-2 d-flex flex-column payable-card-desc">
                  <h1
                    className="produce-amount"
                    style={{ color: "var(--widget-number-color)" }}
                  >
                    {formatToTwoDecimal(widgetData.totalRawMaterials)}
                  </h1>
                </div>
              </div>
            </div>
            <div className="col-sm w-100 p-3 payable-card">
              <div className="w-100 border p-3 shadow-sm rounded h-100">
                <div className=" d-flex flex-row align-items-center payable-icon">
                  <i className="bx bx-wallet fs-3 h-100"></i>
                  <h3>Total Produce</h3>
                </div>
                <div className=" mt-2">
                  <h1
                    className="produce-amount"
                    style={{ color: "var(--widget-number-color)" }}
                  >
                    {formatToTwoDecimal(widgetData.totalProduce)}
                  </h1>
                </div>
              </div>
            </div>
            <div className="col-sm w-100 p-3 payable-card">
              <div className="w-100 border p-3 shadow-sm rounded h-100">
                <div className=" d-flex flex-row align-items-center payable-icon">
                  <i className="bx bx-trending-down fs-3 h-100"></i>
                  <h3>Average Loss</h3>
                </div>
                <div className=" mt-2">
                  <h1
                    className="produce-amount"
                    style={{ color: "var(--widget-number-color-red)" }}
                  >
                    {formatToTwoDecimal(widgetData.averageLoss)}%
                  </h1>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid mt-4">
            <div className="row">
              <div className="col-sm">
                <DateRangePicker
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
              </div>
              <div className="col-sm d-flex flex-row align-items-end  filter-btn-container w-100"></div>
            </div>
            <div className="col-sm mt-3">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="fa-solid fa-sliders"></i>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button
                      className={`dropdown-item ${
                        filterColumn === "all" ? "active" : ""
                      }`}
                      onClick={() => setFilterColumn("all")}
                    >
                      All
                    </button>
                  </li>
                  {[
                    { value: "production_id", label: "Product ID" },
                    { value: "desc", label: "Product Description" },
                    { value: "date_produce", label: "Date Produce" },
                    { value: "total_quantity", label: "Qty In" },
                    { value: "total_produce", label: "Qty Produce" },
                    { value: "loss_percent", label: "Loss (%)" },
                    { value: "loss_quantity", label: "Loss (Qty)" },
                    { value: "status", label: "Status" },
                  ].map(({ value, label }) => (
                    <li key={value}>
                      <button
                        className={`dropdown-item ${
                          filterColumn === value ? "active" : ""
                        }`}
                        onClick={() => setFilterColumn(value)}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="w-100 mt-3 container-fluid">
            <Tabs
              id="production-status-tabs"
              activeKey={activeKeyTab}
              onSelect={handleTabSelect}
              className="mb-3"
            >
              <Tab eventKey="Pending" title="Pending" />
              <Tab eventKey="Approved" title="Approved" />
              <Tab eventKey="Rejected" title="Rejected" />
              <Tab eventKey="All" title="All" />
            </Tabs>
            <DataTable
              columns={sort.sortColumns(columns)}
              data={pagination.data}
              customStyles={customStyles}
              className="dataTable"
              onRowClicked={handleUpdate}
              {...sort.sortProps}
            />
            <PaginationControls {...pagination} />
          </div>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
};

export default NewProduction;
